/**
 * Custom Made / MTO bundle logic for Broadcast (nena_shopify_v3).
 * Ports Shopify_2.0 product-form.js + cart grouping behavior.
 */
(function () {
  'use strict';

  const CART_ADD_URL = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';
  const CART_UPDATE_URL = (window.theme && window.theme.routes && window.theme.routes.cart_update_url) || '/cart/update.js';
  const CART_JS_URL = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';

  const inFlightBundleRequests = (window.__nenaBundleInFlightRequests =
    window.__nenaBundleInFlightRequests || new Set());

  function findBundleRoot(form) {
    if (!form) return null;
    const scope = form.closest('product-info') || form.closest('[data-section-id]') || document;
    return scope.querySelector('[data-bundle-wrapper]');
  }

  function isElementVisible(element) {
    const wrapper = element.closest('[hidden]');
    if (wrapper) return false;
    if (element.hidden) return false;
    if (element.offsetParent === null && getComputedStyle(element).position !== 'fixed') return false;
    return true;
  }

  class MtoBundleForm {
    constructor(form, bundleWrapper) {
      this.form = form;
      this.bundleWrapper = bundleWrapper;
      this.errorMessageWrapper =
        form.closest('product-form')?.querySelector('[data-cart-errors-container]') ||
        form.querySelector('[data-cart-errors-container]');
      this.errorMessage =
        this.errorMessageWrapper?.querySelector('[data-cart-error-message]') ||
        this.errorMessageWrapper?.querySelector('.product-form__error-message');

      this.updateBundleUploadFields();
    }

    refresh(bundleWrapper) {
      this.bundleWrapper = bundleWrapper;
      this.updateBundleUploadFields();
    }

    onBundleVariantChange(evt) {
      const changedInput = evt.target;
      if (changedInput.matches('[data-bundle-upload-input]')) {
        this.syncBundleSelectedLabelFromUpload(changedInput);
        return;
      }

      if (changedInput.matches('[data-bundle-interior-lining-answer]')) {
        const liningWrapper = changedInput.closest('[data-bundle-interior-lining-multiple-wrapper]');
        if (liningWrapper) this.syncInteriorLiningMtoOrderNumbersVisibility(liningWrapper);
        return;
      }

      if (changedInput.matches('[data-bundle-pocket-option-input]')) {
        this.togglePocketUploadField(changedInput.closest('[data-bundle-product]'));
        return;
      }

      if (!changedInput.matches('[data-bundle-variant-id]')) return;

      const fieldset = changedInput.closest('.product-bundle-options__variants');
      if (!fieldset) return;

      const selectedLabel = fieldset.querySelector('[data-bundle-selected-label]');
      if (!selectedLabel) return;

      const selectedVariantLabel = changedInput
        .closest('.product-bundle-options__variant-list')
        .querySelector(
          `label[for="${changedInput.id}"] .product-bundle-options__variant-caption, label[for="${changedInput.id}"] .product-bundle-options__variant-pill`
        );

      if (!selectedVariantLabel) return;

      const selectedVariantText = selectedVariantLabel.textContent.trim();
      selectedLabel.textContent = selectedVariantText;
      this.toggleBundleUploadField(fieldset, selectedVariantText);
      this.toggleBundleInteriorLiningMultipleField(fieldset, selectedVariantText);
      this.toggleBundleAccessoryApplyField(fieldset, selectedVariantText);
      this.toggleBundleOtherSpecifyField(fieldset, selectedVariantText);
      this.toggleBundleLeatherOtherField(fieldset, selectedVariantText);
      this.syncBundleSelectedLabelFromUpload(
        fieldset.closest('[data-bundle-product]')?.querySelector('[data-bundle-upload-input]')
      );
    }

    clearBundleFieldErrors() {
      this.bundleWrapper.querySelectorAll('.field-input--bundle-error').forEach((el) => {
        el.classList.remove('field-input--bundle-error');
      });
      this.bundleWrapper.querySelectorAll('.product-bundle-options__field-group--error').forEach((el) => {
        el.classList.remove('product-bundle-options__field-group--error');
      });
    }

    clearBundleErrorForField(target) {
      if (!target || !this.bundleWrapper.contains(target)) return;

      if (target.type === 'radio' && target.name) {
        const escapeName =
          window.CSS && window.CSS.escape
            ? window.CSS.escape(target.name)
            : target.name.replace(/["\\]/g, '\\$&');
        this.bundleWrapper
          .querySelectorAll(`input[type="radio"][name="${escapeName}"]`)
          .forEach((radio) => {
            const group =
              radio.closest('fieldset') || radio.closest('.product-bundle-options__pockets-radios');
            if (group) group.classList.remove('product-bundle-options__field-group--error');
          });
        return;
      }

      target.classList.remove('field-input--bundle-error');
    }

    validateVisibleBundleFields() {
      this.clearBundleFieldErrors();
      const fieldErrorClass = 'field-input--bundle-error';
      const groupErrorClass = 'product-bundle-options__field-group--error';

      const visibleFieldSelector = [
        'input[type="text"]',
        'input[type="file"]',
        'textarea',
        'select',
        'input[type="checkbox"]',
      ].join(', ');

      const visibleFields = this.bundleWrapper.querySelectorAll(visibleFieldSelector);
      for (const field of visibleFields) {
        if (field.disabled || !isElementVisible(field)) continue;
        if (field.hasAttribute('data-bundle-notes-optional')) continue;
        if (field.hasAttribute('data-bundle-field-optional')) continue;

        if (field.type === 'checkbox') {
          if (!field.checked) field.classList.add(fieldErrorClass);
          continue;
        }

        if (field.type === 'file') {
          if (!field.files || !field.files.length) field.classList.add(fieldErrorClass);
          continue;
        }

        if (!(field.value || '').trim()) field.classList.add(fieldErrorClass);
      }

      const radioGroups = new Map();
      this.bundleWrapper.querySelectorAll('input[type="radio"]').forEach((radio) => {
        if (radio.disabled || !isElementVisible(radio)) return;
        if (!radio.name) return;
        if (!radioGroups.has(radio.name)) radioGroups.set(radio.name, []);
        radioGroups.get(radio.name).push(radio);
      });

      for (const radios of radioGroups.values()) {
        if (!radios.some((radio) => radio.checked)) {
          const group =
            radios[0].closest('fieldset') ||
            radios[0].closest('.product-bundle-options__pockets-radios');
          if (group) group.classList.add(groupErrorClass);
        }
      }

      const hasAnyError = this.bundleWrapper.querySelector(`.${fieldErrorClass}, .${groupErrorClass}`);
      if (hasAnyError) {
        const firstInvalid = this.bundleWrapper.querySelector(`.${fieldErrorClass}`);
        if (firstInvalid && typeof firstInvalid.focus === 'function') {
          try {
            firstInvalid.focus();
          } catch (e) {
            /* no-op */
          }
        }
        return 'Please complete all required fields before adding to cart.';
      }

      return '';
    }

    handleErrorMessage(errorMessage = false) {
      if (!this.errorMessageWrapper || !this.errorMessage) return;
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      this.errorMessageWrapper.style.display = errorMessage ? '' : 'none';
      if (errorMessage) this.errorMessage.textContent = errorMessage;
    }

    getMatchingBundleVariant(variants, selectedVariantId) {
      if (!selectedVariantId) {
        return variants.find((variant) => variant.available) || variants[0];
      }
      return variants.find((variant) => variant.id === selectedVariantId);
    }

    isLeatherOtherSelection(selectedVariantInput) {
      return Boolean(
        selectedVariantInput && selectedVariantInput.dataset.bundleVariantOther === 'true'
      );
    }

    resolveSelectedBundleVariant(bundleProductElement, variants) {
      const selectedVariantInput = bundleProductElement.querySelector(
        '[data-bundle-variant-id]:checked'
      );
      if (!selectedVariantInput) {
        return {
          selectedVariantInput: null,
          selectedVariant: this.getMatchingBundleVariant(variants, null),
          isLeatherOther: false,
        };
      }

      const isLeatherOther = this.isLeatherOtherSelection(selectedVariantInput);
      const selectedVariantId = isLeatherOther
        ? Number.parseInt(selectedVariantInput.dataset.bundleFallbackVariantId || '', 10)
        : Number.parseInt(selectedVariantInput.value, 10);

      return {
        selectedVariantInput,
        selectedVariant: this.getMatchingBundleVariant(
          variants,
          Number.isNaN(selectedVariantId) ? null : selectedVariantId
        ),
        isLeatherOther,
      };
    }

    getLeatherOtherSpecifyValue(bundleProductElement) {
      const input = bundleProductElement.querySelector('[data-bundle-leather-other-input]');
      if (!input || !isElementVisible(input)) return '';
      return (input.value || '').trim();
    }

    getBundleComponentLabel(bundleProductElement, selectedVariant, options = {}) {
      const displayTitle = (bundleProductElement.dataset.bundleDisplayTitle || '').trim();
      const isLeatherOther = Boolean(options.isLeatherOther);
      const leatherOtherValue = options.leatherOtherValue || '';
      const variantTitle = isLeatherOther
        ? leatherOtherValue
          ? `Other: ${leatherOtherValue}`
          : 'Other'
        : (selectedVariant && (selectedVariant.title || '')) || '';
      const fallback = (selectedVariant && (selectedVariant.name || selectedVariant.title)) || 'Bundled item';

      if (!displayTitle) return isLeatherOther ? variantTitle || fallback : fallback;

      if (variantTitle && variantTitle !== 'Default Title') {
        return `${displayTitle} - ${variantTitle}`;
      }

      return displayTitle;
    }

    buildBundleComponents(parentVariantId) {
      const bundleProducts = this.bundleWrapper.querySelectorAll('[data-bundle-product]');
      const bundleComponents = [];

      bundleProducts.forEach((bundleProductElement) => {
        const variantsScript = bundleProductElement.querySelector('[data-bundle-variants]');
        if (!variantsScript) return;

        let variants = [];
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (error) {
          throw new Error('Unable to load bundle variants.');
        }
        if (!variants.length) return;

        const { selectedVariant, isLeatherOther } = this.resolveSelectedBundleVariant(
          bundleProductElement,
          variants
        );
        const leatherOtherValue = this.getLeatherOtherSpecifyValue(bundleProductElement);

        if (!selectedVariant || !selectedVariant.available) {
          throw new Error('One of the bundled products is unavailable.');
        }
        if (parentVariantId && selectedVariant.id === parentVariantId) return;

        bundleComponents.push({
          id: selectedVariant.id,
          title: this.getBundleComponentLabel(bundleProductElement, selectedVariant, {
            isLeatherOther,
            leatherOtherValue,
          }),
          option: isLeatherOther
            ? leatherOtherValue
              ? `Other: ${leatherOtherValue}`
              : 'Other'
            : selectedVariant.title || '',
          sku: selectedVariant.sku || '',
        });
      });

      return bundleComponents;
    }

    appendVisibleProperty(childProperties, input) {
      if (!input || !isElementVisible(input)) return;
      const value = (input.value || '').trim();
      const propertyName = input.getAttribute('data-line-item-property-name');
      if (value && propertyName) childProperties[propertyName] = value;
    }

    buildBundleChildItems(quantity, bundleId, parentVariantId, bundleLabel) {
      const bundleProducts = this.bundleWrapper.querySelectorAll('[data-bundle-product]');
      const childItems = [];

      bundleProducts.forEach((bundleProductElement) => {
        const variantsScript = bundleProductElement.querySelector('[data-bundle-variants]');
        if (!variantsScript) return;

        let variants = [];
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (error) {
          throw new Error('Unable to load bundle variants.');
        }
        if (!variants.length) return;

        const { selectedVariant, isLeatherOther } = this.resolveSelectedBundleVariant(
          bundleProductElement,
          variants
        );
        const leatherOtherValue = this.getLeatherOtherSpecifyValue(bundleProductElement);

        if (!selectedVariant || !selectedVariant.available) {
          throw new Error('One of the bundled products is unavailable.');
        }
        if (parentVariantId && selectedVariant.id === parentVariantId) return;

        const childProperties = {
          _bundle_id: bundleId,
          Bundle: bundleLabel,
          'Bundle item': this.getBundleComponentLabel(bundleProductElement, selectedVariant, {
            isLeatherOther,
            leatherOtherValue,
          }),
        };

        this.appendVisibleProperty(
          childProperties,
          bundleProductElement.querySelector('[data-bundle-exterior-order-input]')
        );
        this.appendVisibleProperty(
          childProperties,
          bundleProductElement.querySelector('[data-bundle-original-order-input]')
        );
        this.appendVisibleProperty(
          childProperties,
          bundleProductElement.querySelector('[data-bundle-other-specify-input]')
        );
        this.appendVisibleProperty(
          childProperties,
          bundleProductElement.querySelector('[data-bundle-leather-other-input]')
        );
        this.appendVisibleProperty(
          childProperties,
          bundleProductElement.querySelector('[data-bundle-exterior-textile-code-input]')
        );

        const interiorLiningFieldset = bundleProductElement.querySelector(
          '[data-bundle-interior-lining-multiple-orders]'
        );
        if (interiorLiningFieldset && isElementVisible(interiorLiningFieldset)) {
          const liningChecked = interiorLiningFieldset.querySelector('input[type="radio"]:checked');
          const liningPropertyName = interiorLiningFieldset.getAttribute(
            'data-line-item-property-name'
          );
          if (liningChecked && liningPropertyName && liningChecked.value) {
            childProperties[liningPropertyName] = liningChecked.value;
          }
        }

        const interiorCustomOrderWrap = bundleProductElement.querySelector(
          '[data-bundle-interior-custom-order-wrap]'
        );
        if (interiorCustomOrderWrap && !interiorCustomOrderWrap.hidden) {
          this.appendVisibleProperty(
            childProperties,
            bundleProductElement.querySelector('[data-bundle-interior-order-input]')
          );
          this.appendVisibleProperty(
            childProperties,
            bundleProductElement.querySelector('[data-bundle-interior-textile-code-input]')
          );
        }

        const mtoOrderNumbersWrap = bundleProductElement.querySelector(
          '[data-bundle-interior-lining-mto-order-numbers-wrap]'
        );
        if (mtoOrderNumbersWrap && !mtoOrderNumbersWrap.hidden) {
          this.appendVisibleProperty(
            childProperties,
            bundleProductElement.querySelector('[data-bundle-interior-lining-mto-order-numbers-input]')
          );
        }

        const bundleUploadField = bundleProductElement.querySelector('[data-bundle-upload-field]');
        const bundleUploadInput = bundleProductElement.querySelector('[data-bundle-upload-input]');
        if (
          bundleUploadField &&
          bundleUploadInput &&
          !bundleUploadField.hidden &&
          bundleUploadInput.files &&
          bundleUploadInput.files[0]
        ) {
          const uploadLinePropertyName = bundleUploadInput.getAttribute(
            'data-line-item-property-name'
          );
          if (uploadLinePropertyName) {
            childProperties[uploadLinePropertyName] = bundleUploadInput.files[0].name;
          }
        }

        this.appendVisibleProperty(
          childProperties,
          !bundleUploadField?.hidden
            ? bundleProductElement.querySelector('[data-bundle-pocket-textile-input]')
            : null
        );

        const pocketsWrapper = bundleProductElement.querySelector('[data-bundle-pockets-wrapper]');
        if (pocketsWrapper && isElementVisible(pocketsWrapper)) {
          const pocketChecked = pocketsWrapper.querySelector(
            '[data-bundle-pocket-option-input]:checked'
          );
          const pocketPropertyName = pocketsWrapper.getAttribute('data-line-item-property-name');
          if (pocketChecked && pocketPropertyName && pocketChecked.value) {
            childProperties[pocketPropertyName] = pocketChecked.value;
          }
        }

        const pocketUploadField = bundleProductElement.querySelector(
          '[data-bundle-pocket-upload-field]'
        );
        const pocketUploadInput = bundleProductElement.querySelector(
          '[data-bundle-pocket-upload-input]'
        );
        if (
          pocketUploadField &&
          pocketUploadInput &&
          !pocketUploadField.hidden &&
          pocketUploadInput.files &&
          pocketUploadInput.files[0]
        ) {
          const pocketUploadName = pocketUploadInput.getAttribute('data-line-item-property-name');
          if (pocketUploadName) {
            childProperties[pocketUploadName] = pocketUploadInput.files[0].name;
          }
        }

        const accessoryApplyWrapper = bundleProductElement.querySelector(
          '[data-bundle-accessory-apply-wrapper]'
        );
        const accessoryApplyFieldset = bundleProductElement.querySelector(
          '[data-bundle-accessory-apply-fieldset]'
        );
        if (accessoryApplyWrapper && accessoryApplyFieldset && !accessoryApplyWrapper.hidden) {
          const accessoryApplyChecked = accessoryApplyFieldset.querySelector(
            'input[type="radio"]:checked'
          );
          const accessoryApplyPropertyName = accessoryApplyFieldset.getAttribute(
            'data-line-item-property-name'
          );
          if (accessoryApplyChecked && accessoryApplyPropertyName && accessoryApplyChecked.value) {
            childProperties[accessoryApplyPropertyName] = accessoryApplyChecked.value;
          }
        }

        childItems.push({
          id: selectedVariant.id,
          quantity,
          properties: childProperties,
        });
      });

      return childItems;
    }

    buildRequestPayload() {
      const formData = new FormData(this.form);
      const data = {};
      let hasFileProperty = false;

      formData.forEach((value, key) => {
        if (key.startsWith('properties[')) {
          const propertyName = key.replace('properties[', '').replace(']', '');
          if (!data.properties) data.properties = {};
          if (value === null || value === undefined || value === '') return;
          if (value instanceof File) {
            if (!value.name) return;
            hasFileProperty = true;
            data.properties[propertyName] = value;
            return;
          }
          data.properties[propertyName] = value;
        } else {
          data[key] = value;
        }
      });

      // Include form-associated fields outside the form element
      this.bundleWrapper.querySelectorAll('[form]').forEach((field) => {
        if (field.disabled || field.type === 'radio' || field.type === 'checkbox') return;
        const name = field.getAttribute('name');
        if (!name || !name.startsWith('properties[')) return;
        const propertyName = name.replace('properties[', '').replace(']', '');
        if (!data.properties) data.properties = {};
        if (field.type === 'file') {
          if (field.files && field.files[0] && field.files[0].name) {
            hasFileProperty = true;
            data.properties[propertyName] = field.files[0];
          }
          return;
        }
        if ((field.value || '').trim()) data.properties[propertyName] = field.value;
      });

      this.bundleWrapper.querySelectorAll('input[type="checkbox"][form], input[type="radio"][form]').forEach(
        (field) => {
          if (!field.checked || field.disabled) return;
          const name = field.getAttribute('name');
          if (!name || !name.startsWith('properties[')) return;
          const propertyName = name.replace('properties[', '').replace(']', '');
          if (!data.properties) data.properties = {};
          data.properties[propertyName] = field.value;
        }
      );

      const quantity = Number.parseInt(data.quantity, 10) || 1;
      const parentVariantId = Number.parseInt(data.id, 10);
      const bundleId = Date.now().toString();
      const bundleLabel = `Bundle ${bundleId.slice(-6)}`;
      const bundleComponents = this.buildBundleComponents(parentVariantId);
      const parentProperties = Object.assign({}, data.properties || {}, {
        _bundle_parent: 'true',
        _bundle_id: bundleId,
        _bundle_config_version: 'non_plus_grouped_visual_v1',
        _bundle_components: JSON.stringify(bundleComponents),
        Bundle: bundleLabel,
        'Bundle item': 'Main item',
      });

      const items = [
        {
          id: data.id,
          quantity,
          properties: parentProperties,
        },
      ];
      const childItems = this.buildBundleChildItems(
        quantity,
        bundleId,
        parentVariantId,
        bundleLabel
      );
      const bundleRequestKey = [
        String(data.id || ''),
        String(quantity || 1),
        String(parentProperties._bundle_components || ''),
      ].join('::');

      const parentData = Object.assign({}, data, { properties: parentProperties });

      if (hasFileProperty) {
        return {
          type: 'multipart',
          parentFormData: this.buildParentFormData(parentData),
          childItems,
          bundleRequestKey,
        };
      }

      return {
        type: 'json',
        body: { items: items.concat(childItems) },
        bundleRequestKey,
      };
    }

    buildParentFormData(parentData) {
      const parentFormData = new FormData();
      parentFormData.append('id', parentData.id);
      parentFormData.append('quantity', String(parentData.quantity || 1));

      Object.entries(parentData.properties || {}).forEach(([propertyName, propertyValue]) => {
        if (propertyValue === null || propertyValue === undefined || propertyValue === '') return;
        parentFormData.append(`properties[${propertyName}]`, propertyValue);
      });

      return parentFormData;
    }

    async submitJsonRequest(body) {
      const response = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const responseJson = await response.json();
      this.throwOnCartError(responseJson);
      return responseJson;
    }

    async submitMultipartBundle(requestPayload) {
      const parentResponse = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: requestPayload.parentFormData,
      });
      const parentResponseJson = await parentResponse.json();
      this.throwOnCartError(parentResponseJson);

      if (!requestPayload.childItems || !requestPayload.childItems.length) {
        return parentResponseJson;
      }

      await this.submitJsonRequest({ items: requestPayload.childItems });
      return parentResponseJson;
    }

    throwOnCartError(responseJson) {
      if (responseJson && responseJson.status) {
        throw new Error(responseJson.description || 'Unable to add bundle to cart.');
      }
    }

    updateBundleUploadFields() {
      const fieldsets = this.bundleWrapper.querySelectorAll('.product-bundle-options__variants');
      fieldsets.forEach((fieldset) => {
        const selectedVariantInput = fieldset.querySelector('[data-bundle-variant-id]:checked');
        if (!selectedVariantInput) return;
        const selectedVariantLabel = selectedVariantInput
          .closest('.product-bundle-options__variant-list')
          .querySelector(
            `label[for="${selectedVariantInput.id}"] .product-bundle-options__variant-caption, label[for="${selectedVariantInput.id}"] .product-bundle-options__variant-pill`
          );
        if (!selectedVariantLabel) return;
        this.toggleBundleUploadField(fieldset, selectedVariantLabel.textContent);
        this.toggleBundleInteriorLiningMultipleField(fieldset, selectedVariantLabel.textContent);
        this.toggleBundleAccessoryApplyField(fieldset, selectedVariantLabel.textContent);
        this.toggleBundleOtherSpecifyField(fieldset, selectedVariantLabel.textContent);
        this.toggleBundleLeatherOtherField(fieldset, selectedVariantLabel.textContent);
        this.togglePocketUploadField(fieldset.closest('[data-bundle-product]'));
      });
    }

    toggleBundleOtherSpecifyField(fieldset, selectedVariantText) {
      const bundleItem = fieldset.closest('[data-bundle-product]');
      if (!bundleItem || bundleItem.dataset.bundleOtherOption !== 'true') return;

      const otherSpecifyWrapper = bundleItem.querySelector('[data-bundle-other-specify-wrapper]');
      const otherSpecifyInput = bundleItem.querySelector('[data-bundle-other-specify-input]');
      if (!otherSpecifyWrapper || !otherSpecifyInput) return;

      const showOtherSpecify = (selectedVariantText || '').trim().toLowerCase() === 'other';
      otherSpecifyWrapper.hidden = !showOtherSpecify;
      otherSpecifyInput.required = showOtherSpecify;
      if (!showOtherSpecify) otherSpecifyInput.value = '';
    }

    toggleBundleLeatherOtherField(fieldset, selectedVariantText) {
      const bundleItem = fieldset.closest('[data-bundle-product]');
      if (!bundleItem || bundleItem.dataset.bundleLeatherOther !== 'true') return;

      const leatherOtherWrapper = bundleItem.querySelector('[data-bundle-leather-other-wrapper]');
      const leatherOtherInput = bundleItem.querySelector('[data-bundle-leather-other-input]');
      if (!leatherOtherWrapper || !leatherOtherInput) return;

      const showLeatherOther = (selectedVariantText || '').trim().toLowerCase() === 'other';
      leatherOtherWrapper.hidden = !showLeatherOther;
      leatherOtherInput.required = showLeatherOther;
      if (!showLeatherOther) leatherOtherInput.value = '';
    }

    toggleBundleAccessoryApplyField(fieldset, selectedVariantText) {
      const bundleItem = fieldset.closest('[data-bundle-product]');
      if (!bundleItem) return;
      const accessoryApplyWrapper = bundleItem.querySelector('[data-bundle-accessory-apply-wrapper]');
      if (!accessoryApplyWrapper) return;

      const normalizedVariantText = (selectedVariantText || '').trim().toLowerCase();
      const showAccessoryApply = normalizedVariantText !== 'no accessories';
      accessoryApplyWrapper.hidden = !showAccessoryApply;
      if (!showAccessoryApply) {
        accessoryApplyWrapper.querySelectorAll('input[type="radio"]').forEach((radio) => {
          radio.checked = false;
        });
      }
    }

    toggleBundleInteriorLiningMultipleField(fieldset, selectedVariantText) {
      const bundleItem = fieldset.closest('[data-bundle-product]');
      if (!bundleItem) return;

      const requiresUploadVariant = /upload/i.test((selectedVariantText || '').trim());

      const customOrderWrap = bundleItem.querySelector('[data-bundle-interior-custom-order-wrap]');
      if (customOrderWrap) {
        customOrderWrap.hidden = !requiresUploadVariant;
        if (!requiresUploadVariant) {
          const interiorOrderInput = customOrderWrap.querySelector('[data-bundle-interior-order-input]');
          const interiorTextileInput = customOrderWrap.querySelector(
            '[data-bundle-interior-textile-code-input]'
          );
          if (interiorOrderInput) interiorOrderInput.value = '';
          if (interiorTextileInput) interiorTextileInput.value = '';
        }
      }

      const wrapper = bundleItem.querySelector('[data-bundle-interior-lining-multiple-wrapper]');
      if (!wrapper) return;

      wrapper.hidden = !requiresUploadVariant;
      if (!requiresUploadVariant) {
        wrapper.querySelectorAll('input[type="radio"]').forEach((radio) => {
          radio.checked = false;
        });
      }
      this.syncInteriorLiningMtoOrderNumbersVisibility(wrapper);
    }

    syncInteriorLiningMtoOrderNumbersVisibility(liningWrapper) {
      const orderNumbersWrap = liningWrapper.querySelector(
        '[data-bundle-interior-lining-mto-order-numbers-wrap]'
      );
      const orderNumbersInput = liningWrapper.querySelector(
        '[data-bundle-interior-lining-mto-order-numbers-input]'
      );
      if (!orderNumbersWrap || !orderNumbersInput) return;

      if (liningWrapper.hidden) {
        orderNumbersWrap.hidden = true;
        orderNumbersInput.value = '';
        return;
      }

      const yesRadio = liningWrapper.querySelector('[data-bundle-interior-lining-answer="yes"]');
      const showOrderNumbers = Boolean(yesRadio && yesRadio.checked);
      orderNumbersWrap.hidden = !showOrderNumbers;
      if (!showOrderNumbers) orderNumbersInput.value = '';
    }

    toggleBundleUploadField(fieldset, selectedVariantText) {
      const bundleItem = fieldset.closest('[data-bundle-product]');
      if (!bundleItem) return;
      const uploadField = bundleItem.querySelector('[data-bundle-upload-field]');
      const uploadInput = bundleItem.querySelector('[data-bundle-upload-input]');
      const selectedLabel = fieldset.querySelector('[data-bundle-selected-label]');
      if (!uploadField || !uploadInput) return;

      const normalizedVariantText = (selectedVariantText || '').trim().toLowerCase();
      const isRequiredPhotoUpload = bundleItem.dataset.requiredPhotoUpload === 'true';
      const isTextileUploadProduct = bundleItem.dataset.textileUploadProduct === 'true';
      const requiresUpload = isRequiredPhotoUpload
        ? true
        : isTextileUploadProduct
          ? normalizedVariantText.includes('enter textile number below')
          : /upload/i.test(normalizedVariantText);

      uploadField.hidden = !requiresUpload;
      uploadInput.required = requiresUpload;
      uploadInput.dataset.variantLabel = (selectedVariantText || '').trim();

      if (!requiresUpload) {
        uploadInput.value = '';
        const pocketTextileInput = bundleItem.querySelector('[data-bundle-pocket-textile-input]');
        if (pocketTextileInput) pocketTextileInput.value = '';
        if (selectedLabel) selectedLabel.textContent = (selectedVariantText || '').trim();
      }
    }

    togglePocketUploadField(bundleItem) {
      if (!bundleItem) return;
      const pocketUploadField = bundleItem.querySelector('[data-bundle-pocket-upload-field]');
      if (!pocketUploadField) return;
      const checked = bundleItem.querySelector('[data-bundle-pocket-option-input]:checked');
      const requiresUpload = checked?.dataset.pocketRequiresUpload === 'true';
      pocketUploadField.hidden = !requiresUpload;
      const pocketUploadInput = pocketUploadField.querySelector('[data-bundle-pocket-upload-input]');
      if (!requiresUpload && pocketUploadInput) pocketUploadInput.value = '';
    }

    syncBundleSelectedLabelFromUpload(uploadInput) {
      if (!uploadInput) return;
      const bundleItem = uploadInput.closest('[data-bundle-product]');
      if (bundleItem && bundleItem.dataset.textileUploadProduct === 'true') return;
      const selectedLabel = bundleItem ? bundleItem.querySelector('[data-bundle-selected-label]') : null;
      if (!selectedLabel) return;
      const file = uploadInput.files && uploadInput.files[0];
      if (file && file.name) {
        selectedLabel.textContent = file.name;
        return;
      }
      selectedLabel.textContent = uploadInput.dataset.variantLabel || selectedLabel.textContent;
    }

    async submit(button) {
      this.handleErrorMessage();
      const bundleValidationMessage = this.validateVisibleBundleFields();
      if (bundleValidationMessage) {
        this.handleErrorMessage(bundleValidationMessage);
        return false;
      }

      let requestPayload;
      try {
        requestPayload = this.buildRequestPayload();
      } catch (error) {
        this.handleErrorMessage(error.message || 'Unable to add bundle to cart.');
        return false;
      }

      const bundleRequestKey = requestPayload.bundleRequestKey || null;
      if (bundleRequestKey) {
        if (inFlightBundleRequests.has(bundleRequestKey)) {
          this.handleErrorMessage('This bundle is already being added. Please wait.');
          return false;
        }
        inFlightBundleRequests.add(bundleRequestKey);
      }

      if (button) {
        button.classList.add('is-loading');
        button.disabled = true;
      }

      try {
        let response;
        if (requestPayload.type === 'multipart') {
          response = await this.submitMultipartBundle(requestPayload);
        } else {
          response = await this.submitJsonRequest(requestPayload.body);
        }

        if (button) {
          button.dispatchEvent(
            new CustomEvent('theme:product:add', {
              detail: { response, button },
              bubbles: true,
            })
          );
        }

        document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
        return true;
      } catch (error) {
        this.handleErrorMessage(error.message || 'Unable to add bundle to cart.');
        if (button) {
          button.dispatchEvent(
            new CustomEvent('theme:product:add-error', {
              detail: { button },
              bubbles: true,
            })
          );
        }
        return false;
      } finally {
        if (bundleRequestKey) inFlightBundleRequests.delete(bundleRequestKey);
        if (button) {
          button.classList.remove('is-loading');
          button.disabled = false;
        }
      }
    }
  }

  const formControllers = new WeakMap();

  function ensureController(form) {
    const bundleWrapper = findBundleRoot(form);
    if (!bundleWrapper) return null;
    if (!formControllers.has(form)) {
      formControllers.set(form, new MtoBundleForm(form, bundleWrapper));
    } else {
      formControllers.get(form).refresh(bundleWrapper);
    }
    return formControllers.get(form);
  }

  // Event delegation so UI still works after product-info HTML swaps
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!target || !target.closest) return;
    const wrapper = target.closest('[data-bundle-wrapper]');
    if (!wrapper) return;

    const form =
      document.getElementById(target.getAttribute('form') || '') ||
      target.closest('form') ||
      wrapper.closest('product-info')?.querySelector('form[data-product-form], form[data-type="add-to-cart-form"]');
    const controller = ensureController(form);
    if (!controller) return;

    controller.clearBundleErrorForField(target);
    controller.onBundleVariantChange(event);
  });

  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!target || !target.closest) return;
    const wrapper = target.closest('[data-bundle-wrapper]');
    if (!wrapper) return;
    const form =
      document.getElementById(target.getAttribute('form') || '') ||
      target.closest('form') ||
      wrapper.closest('product-info')?.querySelector('form[data-product-form], form[data-type="add-to-cart-form"]');
    const controller = ensureController(form);
    if (controller) controller.clearBundleErrorForField(target);
  });

  document.addEventListener(
    'theme:cart:add',
    async (event) => {
      const button = event.detail && event.detail.button;
      const form = button?.form || button?.closest('form');
      const controller = ensureController(form);
      if (!controller) return;

      event.stopImmediatePropagation();
      await controller.submit(button);
    },
    true
  );

  function initForms() {
    document.querySelectorAll('form[data-product-form], form[data-type="add-to-cart-form"]').forEach((form) => {
      ensureController(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initForms);
  } else {
    initForms();
  }

  document.addEventListener('shopify:section:load', initForms);

  /* ---------------------------
   * Cart grouping / qty / remove
   * --------------------------- */

  function parseBundleComponents(item) {
    if (!item?.properties?._bundle_components) return [];
    try {
      const parsed = JSON.parse(item.properties._bundle_components);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat || window.theme.moneyWithCurrencyFormat);
    }
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  function renderBundleComponentsHtml(components) {
    if (!components.length) return '';
    const items = components
      .map((component) => {
        const title = component && (component.title || component.option || component.sku);
        return title ? `<li>${escapeHtml(title)}</li>` : '';
      })
      .filter(Boolean)
      .join('');
    if (!items) return '';
    return `<div class="cart__item-bundle-components" data-mto-bundle-components>
      <span class="cart__item-bundle-components-title">Bundle includes:</span>
      <ul class="cart__item-bundle-components-list">${items}</ul>
    </div>`;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  async function fetchCart() {
    const response = await fetch(`${CART_JS_URL}.js`);
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  function rewriteStrapRepairComponentLabel(label) {
    return String(label || '')
      .replace(/Custom Made\s*-\s*/gi, '')
      .replace(/Custom Made\s+/gi, '')
      .replace(/for your bag/gi, 'for your strap');
  }

  function isStrapRepairBundle(entries) {
    return entries.some((entry) =>
      String(entry.item.product_title || '')
        .toLowerCase()
        .includes('strap repair')
    );
  }

  function getBundleDisplayComponents(parentItem, entries) {
    const componentsFromProps = parseBundleComponents(parentItem);
    const componentsFromChildren = entries
      .filter((entry) => entry.item.key !== parentItem.key)
      .map((entry) => ({
        title: entry.item.product_title,
        option: entry.item.variant_title || '',
      }));
    let components = componentsFromProps.length ? componentsFromProps : componentsFromChildren;

    if (isStrapRepairBundle(entries)) {
      components = components.map((component) => ({
        ...component,
        title: rewriteStrapRepairComponentLabel(component.title),
        option: rewriteStrapRepairComponentLabel(component.option),
      }));
    }

    return components;
  }

  function getItemProperty(item, propertyName) {
    const properties = item && item.properties;
    if (!properties) return '';
    if (properties[propertyName] != null && properties[propertyName] !== '') {
      return properties[propertyName];
    }
    if (Array.isArray(properties)) {
      for (const entry of properties) {
        if (!entry) continue;
        if (Array.isArray(entry) && entry[0] === propertyName) return entry[1] || '';
        if (entry.name === propertyName || entry.key === propertyName) return entry.value || '';
      }
    }
    return '';
  }

  function getDisplayCartItemCount(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    const countedBundleIds = new Set();
    return cart.items.reduce((total, item) => {
      const bundleId = String(getItemProperty(item, '_bundle_id') || '');
      if (bundleId) {
        if (countedBundleIds.has(bundleId)) return total;
        countedBundleIds.add(bundleId);

        const parent = cart.items.find(
          (candidate) =>
            String(getItemProperty(candidate, '_bundle_id') || '') === bundleId &&
            String(getItemProperty(candidate, '_bundle_parent') || '').toLowerCase() === 'true'
        );
        return total + (Number.parseInt((parent || item).quantity, 10) || 0);
      }
      return total + (Number.parseInt(item.quantity, 10) || 0);
    }, 0);
  }

  function excludeChildQuantityInputs(row) {
    if (!row) return;
    row.querySelectorAll('input[name="updates[]"]').forEach((input) => {
      input.setAttribute('data-mto-original-name', 'updates[]');
      input.setAttribute('name', 'mto_bundle_child_qty[]');
      input.setAttribute('data-mto-bundle-child-qty', 'true');
      input.removeAttribute('data-popout-input');
    });
  }

  function syncDisplayCartCounts(cart) {
    const count = getDisplayCartItemCount(cart);
    document.querySelectorAll('cart-count').forEach((el) => {
      el.setAttribute('data-cart-count', String(count));
      const limit = Number.parseInt(el.getAttribute('data-limit') || '', 10);
      el.textContent = limit && count >= limit ? '9+' : String(count);
      el.cartCount = count;
    });
    return count;
  }

  function patchCartItemsCountMethod() {
    document.querySelectorAll('cart-items').forEach((cartItemsEl) => {
      if (cartItemsEl.__mtoBundleCountPatched) return;
      cartItemsEl.__mtoBundleCountPatched = true;
      if (typeof cartItemsEl.getCartItemCount !== 'function') return;

      cartItemsEl.getCartItemCount = function patchedGetCartItemCount() {
        return Array.from(this.cart?.querySelectorAll('input[name="updates[]"]') || []).reduce(
          (total, quantityInput) => {
            const row = quantityInput.closest('[data-item]');
            if (
              row &&
              (row.hasAttribute('data-mto-bundle-child') ||
                row.classList.contains('cart__item--mto-bundle-child'))
            ) {
              return total;
            }
            return total + (Number.parseInt(quantityInput.value, 10) || 0);
          },
          0
        );
      };
    });
  }

  async function applyBundleGrouping() {
    let cart;
    try {
      cart = await fetchCart();
    } catch (e) {
      return;
    }

    patchCartItemsCountMethod();

    const holders = document.querySelectorAll('[data-items-holder], .cart__items, cart-items');
    if (!holders.length) {
      syncDisplayCartCounts(cart);
      return;
    }

    const bundles = {};
    cart.items.forEach((item, index) => {
      const bundleId = getItemProperty(item, '_bundle_id');
      if (!bundleId) return;
      if (!bundles[bundleId]) bundles[bundleId] = [];
      bundles[bundleId].push({ item, line: index + 1 });
    });

    Object.values(bundles).forEach((entries) => {
      if (entries.length < 2) return;

      const parent =
        entries.find(
          (entry) => String(getItemProperty(entry.item, '_bundle_parent') || '').toLowerCase() === 'true'
        ) || entries[0];

      const total = entries.reduce((sum, entry) => sum + (entry.item.final_line_price || 0), 0);
      const memberKeys = entries.map((entry) => entry.item.key);
      const components = getBundleDisplayComponents(parent.item, entries);
      const strapRepairBundle = isStrapRepairBundle(entries);

      holders.forEach((holder) => {
        const parentRow = holder.querySelector(`[data-item="${CSS.escape(parent.item.key)}"]`);
        if (!parentRow) return;

        entries.forEach((entry) => {
          if (entry.item.key === parent.item.key) return;
          const childRow = holder.querySelector(`[data-item="${CSS.escape(entry.item.key)}"]`);
          if (childRow) {
            childRow.classList.add('cart__item--mto-bundle-child');
            childRow.setAttribute('data-mto-bundle-child', 'true');
            childRow.setAttribute('hidden', '');
            excludeChildQuantityInputs(childRow);

            if (strapRepairBundle) {
              const titleEl = childRow.querySelector('.cart__item__title a, .cart__item__title');
              if (titleEl) {
                titleEl.textContent = rewriteStrapRepairComponentLabel(titleEl.textContent.trim());
              }
              childRow
                .querySelectorAll('.cart__item__property span, .cart__item__selected-options span')
                .forEach((span) => {
                  span.textContent = rewriteStrapRepairComponentLabel(span.textContent);
                });
            }
          }
        });

        const encodedKeys = memberKeys.map((key) => encodeURIComponent(key)).join(',');
        parentRow.classList.add('cart__item--mto-bundle-parent');
        parentRow.dataset.mtoBundleKeys = encodedKeys;
        parentRow.dataset.mtoBundleParent = 'true';

        const linePriceEl = parentRow.querySelector('.cart__item__price');
        if (linePriceEl) {
          linePriceEl.textContent = formatMoney(total);
        }

        const content =
          parentRow.querySelector('.cart__item__content-inner') ||
          parentRow.querySelector('.cart__item__content') ||
          parentRow.querySelector('.cart__item__meta');
        if (content) {
          const existing = parentRow.querySelector('[data-mto-bundle-components]');
          const componentsHtml = renderBundleComponentsHtml(components);
          if (existing && componentsHtml) {
            existing.outerHTML = componentsHtml;
          } else if (!existing && componentsHtml) {
            content.insertAdjacentHTML('beforeend', componentsHtml);
          }
        }

        const qtyInput = parentRow.querySelector('input[name="updates[]"]');
        if (qtyInput) {
          qtyInput.dataset.mtoBundleKeys = encodedKeys;
        }

        const removeButton = parentRow.querySelector('[data-item-remove]');
        if (removeButton) {
          removeButton.dataset.mtoBundleKeys = encodedKeys;
        }
      });
    });

    holders.forEach((holder) => {
      holder
        .querySelectorAll('[data-mto-bundle-child], .cart__item--mto-bundle-child')
        .forEach((childRow) => excludeChildQuantityInputs(childRow));
    });

    syncDisplayCartCounts(cart);
  }

  async function refreshBundleCartUi() {
    patchCartItemsCountMethod();
    await applyBundleGrouping();
    try {
      const cart = await fetchCart();
      syncDisplayCartCounts(cart);
    } catch (e) {
      /* no-op */
    }
  }

  async function updateBundleQuantity(memberKeys, quantity) {
    const updates = memberKeys.reduce((acc, key) => {
      acc[key] = quantity;
      return acc;
    }, {});

    await fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });
    document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
  }

  document.addEventListener(
    'theme:cart:update',
    async (event) => {
      const target = event.target;
      const qtyInput =
        (target?.matches?.('input[name="updates[]"]') && target) ||
        target?.querySelector?.('input[name="updates[]"]') ||
        target?.closest?.('quantity-input')?.querySelector('input[name="updates[]"]');
      const row = target?.closest?.('[data-item]');
      const bundleKeysRaw = qtyInput?.dataset?.mtoBundleKeys || row?.dataset?.mtoBundleKeys;
      if (!bundleKeysRaw) return;

      // Bundled items show quantity but do not support qty changes from the cart.
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (qtyInput && event.detail?.quantity != null) {
        qtyInput.value = qtyInput.defaultValue || qtyInput.value;
      }
    },
    true
  );

  document.addEventListener(
    'click',
    async (event) => {
      const removeButton = event.target.closest('[data-item-remove]');
      if (!removeButton) return;
      const bundleKeysRaw = removeButton.dataset.mtoBundleKeys;
      if (!bundleKeysRaw) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const memberKeys = bundleKeysRaw
        .split(',')
        .map((value) => decodeURIComponent(value || ''))
        .filter(Boolean);
      if (!memberKeys.length) return;
      await updateBundleQuantity(memberKeys, 0);
    },
    true
  );

  document.addEventListener('theme:cart:change', () => {
    refreshBundleCartUi();
  });

  document.addEventListener('theme:product:added', () => {
    refreshBundleCartUi();
  });

  document.addEventListener('theme:cart-drawer:open', () => {
    refreshBundleCartUi();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refreshBundleCartUi());
  } else {
    refreshBundleCartUi();
  }
})();
